import type { GeneratedFormDefinition, GeneratedFormValues } from '@template-ui/helpers/generated-form.helpers';
import { visibleGeneratedFields } from '@template-ui/helpers/generated-form.helpers';
import type { WorkflowCaptureField, WorkflowCaptureForm } from './workflow-capture-form';
import type { WorkflowFormConfiguration } from './workflow-form-configuration';
import type { WorkflowFieldPolicySource } from './workflow-runtime-field-policy';
import { isWorkflowFormLookupBinding, workflowFormLookupBindingKey, type WorkflowFormLookupBinding } from './workflow-form-lookup-contract';
export { isWorkflowFormLookupBinding, workflowFormLookupBindingKey, type WorkflowFormLookupBinding, type WorkflowFormLookupAnswer } from './workflow-form-lookup-contract';

export interface WorkflowFormLookupChoice { value: string; label: string; isEnabled: boolean; }
export interface WorkflowFormLookupSnapshot {
  binding: WorkflowFormLookupBinding;
  configuration: {
    configurationVersionId: number; configurationCode: string; versionCode: string;
    cacheRevisionToken: string; asOfUtc: string;
    matchedScopeTypeCode: string; matchedScopeKey?: string | null;
    displayName?: string | null; effectiveStartUtc: string; effectiveEndUtc?: string | null;
    matchedDimensions?: Record<string, string | null> | null;
  };
  revisionToken: string;
  choices: WorkflowFormLookupChoice[];
}
export interface WorkflowFormLookupRead {
  disposition: 'Resolved' | 'Missing' | 'Ambiguous' | 'Exempt';
  reasonCode: string;
  matchedBindingCount: number;
  catalog?: WorkflowFormLookupSnapshot | null;
  exemption?: unknown; trace?: unknown; diagnostics?: unknown;
}
export type WorkflowFormLookupFields = Record<string, WorkflowFormLookupRead>;
export interface WorkflowFormLookupResolution {
  form: GeneratedFormDefinition;
  issues: Record<string, string>;
  revisions: Record<string, string>;
  key: string | null;
}
export type WorkflowCaptureLookupResolution = WorkflowFormLookupResolution;
interface LookupDeclaration { name: string; binding: WorkflowFormLookupBinding; }

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const bounded = (value: unknown): value is string => typeof value === 'string' && !!value.trim() && value === value.trim() && value.length <= 120;
const revision = (value: unknown): value is string => typeof value === 'string' && value.length === 64 && /^[a-f0-9]{64}$/.test(value);
const positive = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const instant = (value: unknown): value is string => typeof value === 'string' && value.endsWith('Z') && Number.isFinite(Date.parse(value));

function snapshot(value: unknown, binding: WorkflowFormLookupBinding): WorkflowFormLookupSnapshot | null {
  if (!object(value) || Object.keys(value).some(key => !['binding', 'configuration', 'revisionToken', 'choices'].includes(key))
    || !isWorkflowFormLookupBinding(value['binding']) || workflowFormLookupBindingKey(value['binding']) !== workflowFormLookupBindingKey(binding)
    || !revision(value['revisionToken']) || !object(value['configuration']) || !Array.isArray(value['choices'])
    || value['choices'].length < 1 || value['choices'].length > 100) return null;
  const configuration = value['configuration'];
  if (Object.keys(configuration).some(key => !['configurationVersionId', 'configurationCode', 'versionCode', 'cacheRevisionToken', 'asOfUtc', 'matchedScopeTypeCode', 'matchedScopeKey', 'displayName', 'effectiveStartUtc', 'effectiveEndUtc', 'matchedDimensions'].includes(key))
    || !positive(configuration['configurationVersionId']) || !bounded(configuration['configurationCode'])
    || configuration['configurationCode'].toLowerCase() !== binding.configurationCode.toLowerCase()
    || !bounded(configuration['versionCode']) || typeof configuration['cacheRevisionToken'] !== 'string' || !configuration['cacheRevisionToken']
    || !instant(configuration['asOfUtc']) || !instant(configuration['effectiveStartUtc'])
    || !bounded(configuration['matchedScopeTypeCode'])
    || configuration['matchedScopeKey'] != null && !bounded(configuration['matchedScopeKey'])
    || configuration['effectiveEndUtc'] != null && !instant(configuration['effectiveEndUtc'])) return null;
  if (value['choices'].some(choice => !object(choice) || Object.keys(choice).some(key => !['value', 'label', 'isEnabled'].includes(key))
    || !bounded(choice['value']) || !bounded(choice['label']) || typeof choice['isEnabled'] !== 'boolean')
    || new Set(value['choices'].map(choice => choice['value'])).size !== value['choices'].length) return null;
  return value as unknown as WorkflowFormLookupSnapshot;
}

/** One snapshot validator serves capture and installed provided-field declarations without reads or caching. */
function resolveLookupFields(
  definition: GeneratedFormDefinition,
  declared: readonly LookupDeclaration[],
  identity: unknown,
  lookupFields: unknown,
  source?: WorkflowFieldPolicySource,
): WorkflowFormLookupResolution | null {
  if (!declared.length) return lookupFields == null || object(lookupFields) && !Object.keys(lookupFields).length
    ? { form: definition, issues: {}, revisions: {}, key: null } : null;
  const expected = declared.filter(field => definition.fields.some(item => item.name === field.name));
  const rows = object(lookupFields) ? lookupFields : null;
  if (rows && Object.keys(rows).some(id => !expected.some(field => field.name === id))) return null;
  const issues: Record<string, string> = {}, revisions: Record<string, string> = {}, signature: unknown[] = [];
  const fields = definition.fields.map(field => {
    const declaration = expected.find(item => item.name === field.name);
    if (!declaration) return field;
    const row = rows?.[field.name];
    const valid = object(row) && !Object.keys(row).some(key => !['disposition', 'reasonCode', 'matchedBindingCount', 'catalog', 'exemption', 'trace', 'diagnostics'].includes(key))
      && ['Resolved', 'Missing', 'Ambiguous', 'Exempt'].includes(String(row['disposition']))
      && typeof row['reasonCode'] === 'string' && row['reasonCode'].length <= 200
      && typeof row['matchedBindingCount'] === 'number' && Number.isSafeInteger(row['matchedBindingCount']) && row['matchedBindingCount'] >= 0;
    const catalog = valid && row['disposition'] === 'Resolved' ? snapshot(row['catalog'], declaration.binding) : null;
    if (!catalog) {
      const state = valid ? row['disposition'] : 'Unavailable';
      issues[field.name] = state === 'Ambiguous'
        ? 'More than one lookup source matched. Refresh current state before continuing.'
        : state === 'Exempt' ? 'This lookup source is not available in the current context. Refresh current state before continuing.'
          : 'Lookup choices are unavailable. Refresh current state to try again.';
      signature.push([field.name, workflowFormLookupBindingKey(declaration.binding), state, valid ? row['reasonCode'] : null]);
      return { ...field, options: [], disabled: true };
    }
    revisions[field.name] = catalog.revisionToken;
    signature.push([field.name, workflowFormLookupBindingKey(declaration.binding), catalog.revisionToken,
      catalog.configuration.configurationVersionId, catalog.configuration.versionCode, catalog.configuration.cacheRevisionToken,
      catalog.configuration.matchedScopeTypeCode, catalog.configuration.matchedScopeKey ?? null, catalog.choices]);
    if (!catalog.choices.some(choice => choice.isEnabled)) issues[field.name] = 'No enabled lookup choices are available. Refresh current state to try again.';
    return { ...field, options: [
      ...(!field.required ? [{ value: '', label: 'Not provided', labelToken: '' }] : []),
      ...catalog.choices.map(choice => ({ value: choice.value, label: choice.label, labelToken: '', ...(choice.isEnabled ? {} : { disabled: true }) })),
    ] };
  });
  return { form: { ...definition, fields }, issues, revisions, key: JSON.stringify([source ?? null, identity, signature]) };
}

/** Apply only choices supplied by the authorized capture-action offer. */
export function resolveCaptureLookupFields(definition: GeneratedFormDefinition, form: WorkflowCaptureForm | null | undefined,
  lookupFields: unknown, source?: WorkflowFieldPolicySource): WorkflowCaptureLookupResolution | null {
  const declared = form?.fields.filter(field => field.lookup != null).map(field => ({ name: field.id, binding: field.lookup! })) ?? [];
  if (declared.length && form?.version !== 3) return null;
  return resolveLookupFields(definition, declared, form?.id, lookupFields, source);
}

function providedDeclarations(configuration: WorkflowFormConfiguration | null | undefined): LookupDeclaration[] | null {
  if (configuration == null) return [];
  if (!Array.isArray(configuration.fields)) return null;
  const fields = configuration.fields.filter(field => field?.optionSource === 'lookup' || field?.lookup != null);
  if (fields.length && (configuration.version !== 3 || fields.some(field => !field || typeof field.name !== 'string'
    || field.optionSource !== 'lookup' || field.options != null || !isWorkflowFormLookupBinding(field.lookup)))) return null;
  const declared = fields.map(field => ({ name: field.name, binding: field.lookup! }));
  return new Set(declared.map(field => field.name)).size === declared.length ? declared : null;
}

/** Provided declarations use installed names; choices still come only from the current authorized offer. */
export function resolveProvidedLookupFields(definition: GeneratedFormDefinition, configuration: WorkflowFormConfiguration | null | undefined,
  lookupFields: unknown, source?: WorkflowFieldPolicySource): WorkflowFormLookupResolution | null {
  const declared = providedDeclarations(configuration);
  return declared ? resolveLookupFields(definition, declared, ['provided', definition.contract, definition.version, configuration ?? null], lookupFields, source) : null;
}

/** An unused optional or hidden source does not block other valid answers. */
export function activeCaptureLookupIssues(
  definition: GeneratedFormDefinition,
  issues: Record<string, string>,
  values: GeneratedFormValues,
): Record<string, string> {
  return Object.fromEntries(visibleGeneratedFields(definition, values)
    .filter(field => issues[field.name] && (field.required || values[field.name] !== undefined && values[field.name] !== null && values[field.name] !== ''))
    .map(field => [field.name, issues[field.name]]));
}

/** Tokens are copied from the current offer only for visible, nonblank lookup answers. */
export function captureLookupRevisions(
  definition: GeneratedFormDefinition,
  form: WorkflowCaptureForm,
  revisions: Record<string, string>,
  values: GeneratedFormValues,
): Record<string, string> | null {
  return lookupRevisions(definition, form.fields.filter(field => field.lookup != null).map(field => ({ name: field.id, binding: field.lookup! })), revisions, values);
}
function lookupRevisions(definition: GeneratedFormDefinition, declared: readonly LookupDeclaration[], revisions: Record<string, string>, values: GeneratedFormValues,
  editableOnly = false): Record<string, string> | null {
  const result: Record<string, string> = {};
  for (const field of visibleGeneratedFields(definition, values)) {
    if (!declared.some(item => item.name === field.name) || editableOnly && field.readOnly) continue;
    const value = values[field.name];
    if (value === undefined || value === null || value === '') continue;
    if (editableOnly && field.disabled) return null;
    if (!bounded(value) || !revision(revisions[field.name]) || !field.options?.some(option => option.value === value && option.disabled !== true)) return null;
    result[field.name] = revisions[field.name];
  }
  return result;
}

/** Only visible editable nonblank provided answers carry offered revisions; transport adapters choose their closed scalar fields. */
export function providedLookupRevisions(definition: GeneratedFormDefinition, configuration: WorkflowFormConfiguration | null | undefined,
  revisions: Record<string, string>, values: GeneratedFormValues): Record<string, string> | null {
  const declared = providedDeclarations(configuration);
  return declared ? lookupRevisions(definition, declared, revisions, values, true) : null;
}

/** Historical labels are evidence, never a new lookup read or a current-catalogue substitution. */
export function captureLookupAnswerLabel(field: WorkflowCaptureField, value: unknown, evidence: unknown): string {
  if (value === undefined || value === null || value === '') return 'Not provided';
  if (field.lookup && object(evidence) && !Object.keys(evidence).some(key => !['value', 'label', 'configurationCode', 'domainCode', 'configurationVersionId', 'revisionToken', 'resolvedAtUtc'].includes(key))
    && bounded(value) && evidence['value'] === value && bounded(evidence['label'])
    && bounded(evidence['configurationCode']) && evidence['configurationCode'].toLowerCase() === field.lookup.configurationCode.toLowerCase()
    && bounded(evidence['domainCode']) && evidence['domainCode'].toLowerCase() === field.lookup.domainCode.toLowerCase()
    && positive(evidence['configurationVersionId']) && revision(evidence['revisionToken']) && instant(evidence['resolvedAtUtc'])) return evidence['label'];
  return `${String(value)} (lookup label unavailable)`;
}
